import { TeacherPage } from "@/components/teacher/TeacherPage";
import { LessonTreeCard } from "@/components/teacher/score/LessonTreeCard";
import { ReportLessonTreeDocument } from "@/generated/graphql";
import { useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { StudentBreadcrumb } from "./StudentBreadcrumb";
import type { ClassroomScope } from "./classroomScope";
import { useStudentContext } from "./useStudentContext";

/** Every slide the student has been set, scored — the same tiles their own app shows them. */
export function StudentScorePage({
  classroomId,
  studentId,
  scope = "teacher",
}: { classroomId: string; studentId: string; scope?: ClassroomScope }) {
  const t = useT();
  const { classroomName, student } = useStudentContext(scope, classroomId, studentId);

  // The folders walked into, outermost first. Empty reads the root level.
  const [path, setPath] = useState<{ projectId: string; name: string }[]>([]);

  // One level at a time: a level carries every slide of every lesson in it, so opening a folder
  // is what fetches its contents rather than the page loading the whole tree.
  const lessonTreeQuery = useQuery(ReportLessonTreeDocument, {
    variables: { studentId, parentId: path[path.length - 1]?.projectId ?? null },
  });

  const subtitle = [student?.username, student?.englishName].filter(Boolean).join(" · ");

  return (
    <TeacherPage
      title={student?.name ?? t["student.fallbackName"]}
      subtitle={subtitle || undefined}
      embedded={scope === "admin"}
      breadcrumb={
        <StudentBreadcrumb
          scope={scope}
          classroomId={classroomId}
          classroomName={classroomName}
          studentId={studentId}
          studentName={student?.name ?? null}
          trail={[t["student.score"]]}
        />
      }
    >
      <LessonTreeCard
        level={lessonTreeQuery.data?.reportLessonTree}
        path={path}
        onEnterProject={(project) => setPath((prev) => [...prev, project])}
        onBack={() => setPath((prev) => prev.slice(0, -1))}
        loading={lessonTreeQuery.loading && !lessonTreeQuery.data}
        failed={!!lessonTreeQuery.error && !lessonTreeQuery.data}
      />
    </TeacherPage>
  );
}
